import { __decorate } from "tslib";
import { consume } from '@lit/context';
import { msg } from '@lit/localize';
import { html } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { customElement, property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { live } from 'lit/directives/live.js';
import { SlotController } from '../../shared/controllers/slot.controller.js';
import { HelixFormControl } from '../../shared/forms/helix-form-control.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { formControlStyles } from '../../shared/styles/form-control.styles.js';
import '../icon/index.js';
import { toggleGroupDisabledContext, toggleGroupReadonlyContext } from './toggle-group-context.js';
import { toggleStyles } from './toggle.styles.js';
import { createValidity, sameValidity, submitForm } from '../../shared/forms/form-utils.js';
let HlxToggle = class HlxToggle extends HelixFormControl {
    constructor() {
        super(...arguments);
        this.label = '';
        this.description = '';
        this.checked = false;
        this.indeterminate = false;
        this.readonly = false;
        this.title = '';
        this.inputId = crypto.randomUUID();
        this.slotController = new SlotController(this, 'label', 'description');
    }
    renderIcon() {
        if (this.indeterminate) {
            return html `
                <hlx-icon name="minus" decorative size="16" class="hlx-toggle-icon"></hlx-icon>
            `;
        }
        if (this.checked) {
            return html `
                <hlx-icon
                    name="circle-check-filled"
                    decorative
                    size="16"
                    class="hlx-toggle-icon"></hlx-icon>
            `;
        }
        return html `
            <hlx-icon name="circle-xmark" size="16" decorative class="hlx-toggle-icon"></hlx-icon>
        `;
    }
    render() {
        const disabled = this.disabled || this.disabledByGroup;
        const readonly = this.readonly || this.readonlyByGroup;
        const showLabel = this.label || this.slotController.hasSlot('label');
        const showDescription = this.description || this.slotController.hasSlot('description');
        return html `
            <div
                part="hlx-form-control"
                class="${classMap({
            ...this.validation.classMap,
            'hlx-form-control-md': true,
            'hlx-toggle-has-default-slot': this.slotController.hasSlot('[default]'),
            'hlx-toggle-has-label': showLabel,
            'hlx-toggle-has-description': showDescription,
        })}">
                <input
                    id=${this.inputId}
                    type="checkbox"
                    role="switch"
                    part="input"
                    class="hlx-toggle"
                    aria-checked="${this.indeterminate ? 'mixed' : String(Boolean(this.checked))}"
                    aria-readonly="${String(Boolean(readonly))}"
                    .checked=${live(this.checked)}
                    .indeterminate=${live(this.indeterminate)}
                    ?disabled=${disabled}
                    ?readonly=${readonly}
                    name=${ifDefined(this.name)}
                    value=${ifDefined(this.value)}
                    @change=${this.handleChange}
                    @click=${this.handleClick}
                    @keydown=${this.handleKeydown}
                    @focus=${this.handleFocus}
                    @blur=${this.handleBlur} />
                <label
                    for=${this.inputId}
                    part="container"
                    class=${classMap({
            'hlx-toggle-container': true,
            'hlx-toggle-disabled': disabled,
            'hlx-toggle-readonly': readonly,
            'hlx-toggle-checked': this.checked && !this.indeterminate,
            'hlx-toggle-indeterminate': this.indeterminate,
        })}
                    title="${ifDefined(this.title)}">
                    <span
                        part="toggle"
                        class="hlx-toggle-control hlx-animation-with-prefers-reduced-motion">
                        <span
                            class="hlx-toggle-icon-wrapper hlx-animation-with-prefers-reduced-motion">
                            ${this.renderIcon()}
                        </span>
                    </span>
                    <div class="hlx-toggle-secondary-container">
                        <slot name="label" part="label" class="hlx-toggle-label">
                            ${this.label}
                        </slot>
                        <slot name="description" part="description" class="hlx-toggle-description">
                            ${this.description}
                        </slot>
                    </div>
                </label>
                <div class="hlx-toggle-content">
                    <slot part="default-slot" class="hlx-default-slot"></slot>
                    ${this.validation.renderErrorSlot('hlx-toggle-error')}
                </div>
            </div>
        `;
    }
    get validity() {
        if (this.validation.hasCustomValidity) {
            return createValidity('customError');
        }
        if (this.required && !this.checked) {
            return createValidity('valueMissing');
        }
        return createValidity();
    }
    getDefaultErrorMessage(validity) {
        if (validity.valueMissing) {
            return msg('Please flip this toggle if you want to proceed.');
        }
        return '';
    }
    handleChange() {
        const { checked, indeterminate } = this.toggleNode;
        this.checked = checked;
        this.indeterminate = indeterminate;
        if (this.checked) {
            const validity = this.validity;
            if (validity.valid || !sameValidity(validity, this.lastValidity)) {
                // hide errors immediately
                this.validation.showError = false;
                this.validation.errorText = '';
            }
            if (!this.value) {
                this.value = 'on';
            }
        }
        this.emit('hlx-change', {
            checked,
            indeterminate,
        });
    }
    handleClick(event) {
        if (this.readonly || this.readonlyByGroup) {
            event.preventDefault();
        }
    }
    focus(options) {
        this.toggleNode.focus(options);
    }
    handleKeydown(e) {
        if (e.key === 'Enter') {
            submitForm(this.getForm());
        }
    }
    handleBlur() {
        this.emit('hlx-blur');
    }
    handleFocus() {
        this.emit('hlx-focus');
    }
};
HlxToggle.styles = [componentStyles, formControlStyles, toggleStyles];
__decorate([
    query('input')
], HlxToggle.prototype, "toggleNode", void 0);
__decorate([
    property({ type: String })
], HlxToggle.prototype, "label", void 0);
__decorate([
    property({ type: String })
], HlxToggle.prototype, "description", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxToggle.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxToggle.prototype, "indeterminate", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxToggle.prototype, "readonly", void 0);
__decorate([
    property()
], HlxToggle.prototype, "title", void 0);
__decorate([
    consume({ context: toggleGroupDisabledContext, subscribe: true })
], HlxToggle.prototype, "disabledByGroup", void 0);
__decorate([
    consume({ context: toggleGroupReadonlyContext, subscribe: true })
], HlxToggle.prototype, "readonlyByGroup", void 0);
HlxToggle = __decorate([
    customElement('hlx-toggle')
], HlxToggle);
export { HlxToggle };
//# sourceMappingURL=toggle.js.map